/**
 * Opaque, DEVICE-LOCAL aliases for accounts — the payload of the `?as=`
 * handoff that carries the active account into a NEW tab.
 *
 * ── Why an alias and not the userId ─────────────────────────────────────────
 * Per-tab accounts live in `sessionStorage` (see `lib/tab-account.ts`), which a
 * tab opened via ctrl+click / `target="_blank" rel="noopener"` does NOT inherit:
 * that tab would land on the device's default account instead of the one the
 * link was clicked from. The app's own "open in new tab" affordances therefore
 * append a one-shot `?as=<alias>` that the new tab consumes and immediately
 * strips from the URL.
 *
 * That parameter exists in the address bar for a few milliseconds, so it must
 * be safe if it IS copied. A `userId` would leak a stable, cross-device
 * identifier into a shareable string. An alias is a random value minted on THIS
 * device and resolvable ONLY against THIS device's map: copied anywhere else it
 * resolves to nothing and is ignored (the tab falls back to its default
 * account), which is exactly the failure mode we want.
 *
 * The map is pruned against the registry on every read, so an alias dies with
 * its account.
 */

import { generateSecretToken } from "@fleet/utils";

const ALIAS_STORAGE_KEY = "account_aliases";

/** alias → userId. Device-local; never leaves this browser profile. */
type AliasMap = Record<string, string>;

function readAliasMap(): AliasMap {
  try {
    const raw = globalThis.localStorage.getItem(ALIAS_STORAGE_KEY);
    if (!raw) {
      return {};
    }
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
      return {};
    }
    const map: AliasMap = {};
    for (const [alias, userId] of Object.entries(parsed)) {
      if (
        typeof alias === "string" &&
        typeof userId === "string" &&
        alias !== "" &&
        userId !== ""
      ) {
        map[alias] = userId;
      }
    }
    return map;
  } catch {
    return {};
  }
}

function writeAliasMap(map: AliasMap): void {
  try {
    globalThis.localStorage.setItem(ALIAS_STORAGE_KEY, JSON.stringify(map));
  } catch {
    // Storage unavailable — the handoff degrades to "new tab uses the default
    // account", which is the pre-handoff behavior. Never fatal.
  }
}

/** Mints a random, non-guessable alias. */
function mintAlias(): string {
  return generateSecretToken();
}

/**
 * The alias for `userId`, minting and persisting one on first use. Stable for
 * the life of the account on this device, so repeated links are identical.
 */
export function aliasForAccount(userId: string): string {
  const map = readAliasMap();
  for (const [alias, id] of Object.entries(map)) {
    if (id === userId) {
      return alias;
    }
  }
  const alias = mintAlias();
  map[alias] = userId;
  writeAliasMap(map);
  return alias;
}

/**
 * Resolves a `?as=` alias back to a userId on THIS device, or `null` when the
 * alias is unknown — which is the normal outcome for a link that travelled to
 * another device, and is handled by simply ignoring the parameter.
 */
export function accountIdForAlias(alias: string): string | null {
  return readAliasMap()[alias] ?? null;
}

/**
 * Drops aliases whose account no longer exists, so the map cannot grow without
 * bound and an alias never outlives its account. Called after registry
 * mutations; a no-op when nothing changed.
 */
export function pruneAliases(knownUserIds: readonly string[]): void {
  const map = readAliasMap();
  const known = new Set(knownUserIds);
  let changed = false;
  for (const [alias, userId] of Object.entries(map)) {
    if (!known.has(userId)) {
      delete map[alias];
      changed = true;
    }
  }
  if (changed) {
    writeAliasMap(map);
  }
}
